import React from "react";
import { usePageSeo } from "../utils/seo";

const sections = [
  {
    title: "1. Information We May Collect",
    content: [
      "We may collect the following types of information:",
      "Personal information you provide directly:",
    ],
    list: [
      "Name and surname",
      "Phone number",
      "Email address",
      "Delivery address",
      "Order details",
      "Messages or service requests you send to us",
    ],
    afterList: [
      "Technical and website information:",
      "IP address, browser type, device type, pages visited, date and time of visit, cookies and similar tracking technologies.",
      "Business and service information:",
      "Product inquiries, repair/service requests, warranty-related information, and communication history related to orders or support.",
    ],
  },
  {
    title: "2. How We Use Your Information",
    content: ["We may use your information to:"],
    list: [
      "Process and manage orders",
      "Contact you about your order or service request",
      "Provide customer support",
      "Arrange delivery or pickup",
      "Improve our website and services",
      "Publish and manage website content",
      "Prevent fraud, spam, and unauthorized activity",
      "Comply with legal obligations",
      "Run marketing, analytics, or advertising activities where allowed by law",
    ],
  },
  {
    title: "3. Facebook / Meta API Usage",
    content: [
      "Buzz Mobile may use Meta/Facebook API tools only for managing and publishing content on official Buzz Mobile Facebook Page(s).",
      "Our application may use a Facebook Page access token to create and publish posts on our Facebook Page, share blog posts, product updates, promotions, or website links, and manage Page content created by Buzz Mobile.",
      "We do not use Facebook Login for website visitors.",
      "We do not collect personal Facebook profile data from visitors or customers through this application.",
      "We do not sell, rent, or share Facebook user data with third parties.",
      "Any content published through the Meta/Facebook API is created or approved by Buzz Mobile and is used only for managing our own Facebook Page presence.",
    ],
  },
  {
    title: "4. Cookies and Tracking Technologies",
    content: [
      "Our website may use cookies or similar technologies to improve user experience, remember preferences, analyze website traffic, and support marketing or advertising features.",
      "You can control or disable cookies through your browser settings. Some website features may not work properly if cookies are disabled.",
    ],
  },
  {
    title: "5. Third-Party Services",
    content: [
      "We may use third-party services for hosting, analytics, advertising, payment processing, delivery, communication, or social media integrations.",
      "These third parties may process limited information according to their own privacy policies and only as needed to provide their services.",
      "Examples may include website hosting providers, analytics tools, Meta/Facebook tools, Google services, and payment or delivery providers, if applicable.",
    ],
  },
  {
    title: "6. How We Share Information",
    content: [
      "We do not sell your personal information.",
      "We may share information only when necessary with delivery providers, payment processors if applicable, IT, hosting, or website service providers, legal authorities if required by law, and business partners only when needed to complete your order or service request.",
    ],
  },
  {
    title: "7. Data Retention",
    content: [
      "We keep personal information only for as long as necessary to provide our services, complete orders, respond to inquiries, comply with legal obligations, resolve disputes, and maintain business records.",
      "When information is no longer needed, we will delete it or anonymize it where reasonably possible.",
    ],
  },
  {
    title: "8. International Visitors",
    content: [
      "Our website may be accessed by users from different countries. By using our website or contacting us, you understand that your information may be processed in North Macedonia or by service providers located in other countries.",
      "We take reasonable steps to protect personal information according to applicable privacy and data protection laws.",
    ],
  },
  {
    title: "9. Your Privacy Rights",
    content: ["Depending on your location, you may have the right to:"],
    list: [
      "Request access to your personal information",
      "Request correction of inaccurate information",
      "Request deletion of your information",
      "Object to or restrict certain processing",
      "Withdraw consent where processing is based on consent",
      "Request information about how your data is used",
    ],
    afterList: ["To make a privacy request, contact us using the details below."],
  },
  {
    title: "10. Children's Privacy",
    content: [
      "Our website and services are not intended for children under the age of 13. We do not knowingly collect personal information from children.",
      "If you believe a child has provided us with personal information, please contact us and we will take appropriate steps to remove it.",
    ],
  },
  {
    title: "11. Data Security",
    content: [
      "We use reasonable technical and organizational measures to protect personal information from unauthorized access, loss, misuse, alteration, or disclosure.",
      "However, no online service or method of electronic storage is 100% secure, so we cannot guarantee absolute security.",
    ],
  },
  {
    title: "12. Links to Other Websites",
    content: [
      "Our website may contain links to third-party websites or social media platforms. We are not responsible for the privacy practices, content, or security of those external websites.",
    ],
  },
  {
    title: "13. Changes to This Privacy Policy",
    content: [
      'We may update this Privacy Policy from time to time. Any changes will be published on this page with an updated "Last updated" date.',
    ],
  },
];

function PrivacyPolicy() {
  usePageSeo({
    title: "Privacy Policy | Buzz Mobile",
    description:
      "Privacy Policy for Buzz Mobile, including how customer, website, order, service, and Meta/Facebook API information may be used.",
    canonicalPath: "/privacy-policy",
  });

  return (
    <main className="privacy-policy-page">
      <section className="container privacy-policy">
        <header className="privacy-policy__header">
          <span>Last updated: July 2, 2026</span>
          <h1>Privacy Policy</h1>
          <p>
            Buzz Mobile respects your privacy. This Privacy Policy explains how
            we collect, use, store, and protect information when you visit our
            website, contact us, place an order, use our services, or interact
            with our online content.
          </p>
          <p>
            Buzz Mobile is a mobile phone shop and service provider based in
            Strumica, North Macedonia.
          </p>
        </header>

        <div className="privacy-policy__content">
          {sections.map((section) => (
            <section key={section.title}>
              <h2>{section.title}</h2>
              {(section.content || []).map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              {section.list ? (
                <ul>
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
              {(section.afterList || []).map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </section>
          ))}

          <section>
            <h2>14. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or want to request
              access, correction, or deletion of your data, you can contact us
              at:
            </p>
            <address>
              <strong>Buzz Mobile</strong>
              <span>Strumica, North Macedonia</span>
              <a href="mailto:buzzmobiledooel@gmail.com">
                buzzmobiledooel@gmail.com
              </a>
              <a href="https://buzzmobile.mk/">https://buzzmobile.mk/</a>
            </address>
          </section>
        </div>
      </section>
    </main>
  );
}

export default PrivacyPolicy;
